import { motion } from 'motion/react';
import { cn } from './util';

export interface ProgressSquaresProps {
  /** Number of completed steps (rendered ink-filled). */
  done: number;
  total: number;
  /** Highlight the current (next) step in yellow. Default true. */
  showCurrent?: boolean;
  size?: 'sm' | 'md';
  /** Show the "n/total" label next to the squares. */
  showLabel?: boolean;
  label?: string;
  className?: string;
}

/** Row of ink squares: ■■■▣□□□ — done / current / remaining (jury item progress, rounds). */
export function ProgressSquares({
  done,
  total,
  showCurrent = true,
  size = 'md',
  showLabel,
  label,
  className,
}: ProgressSquaresProps) {
  const current = Math.min(done, total - 1);
  const shown = showCurrent ? Math.min(done + 1, total) : done;
  return (
    <div
      role="progressbar"
      aria-label={label ?? 'İlerleme'}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-valuetext={`${shown}/${total}`}
      className={cn('inline-flex items-center gap-2', className)}
    >
      <span className="inline-flex gap-[3px]">
        {Array.from({ length: total }, (_, i) => {
          const isDone = i < done;
          const isCurrent = showCurrent && i === current && done < total;
          return (
            <motion.span
              key={i}
              initial={false}
              animate={
                isCurrent ? { scale: [1, 1.18, 1], rotate: [0, -6, 0] } : { scale: 1, rotate: 0 }
              }
              transition={{ duration: 0.45 }}
              className={cn(
                'block border-2 border-ink',
                size === 'sm' ? 'size-3.5' : 'size-[18px]',
                isDone ? 'bg-ink' : isCurrent ? 'bg-yellow' : 'bg-white',
              )}
            />
          );
        })}
      </span>
      {showLabel && (
        <span className={cn('comic tabular-nums', size === 'sm' ? 'text-[15px]' : 'text-[19px]')}>
          {shown}/{total}
        </span>
      )}
    </div>
  );
}
